import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader, Card, EmptyState } from "@/components/ui-helpers";
import { inputCls, Field } from "./Projects";
import { toast, Toaster } from "sonner";
import {
  Plus, Search, Paperclip, ClipboardList, ArrowUpDown, RotateCw,
  ClipboardEdit, Eye, Inbox, Files, ShieldCheck, CheckCircle2, LayoutDashboard,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { usePaginatedList } from "@/hooks/usePaginatedList";
import { PaginationControls } from "@/components/PaginationControls";
import { useFormTab } from "@/hooks/useFormTab";
import OneNotesDetail from "./OneNotesDetail";
import { formatINR } from "@/lib/api";
import { formatUser, useUserDirectory } from "@/components/UserLabel";

// Jul-2026 — resolve WHO a note is currently waiting on, from fields the
// list row already carries (approvers / initiator / active query).
function pendingWithName(r) {
  const pid = r.pending_with_user_id || "";
  if (!pid) return "";
  if (pid === r.initiated_by_user_id || pid === r.initiated_by_email) {
    return `${r.initiated_by_name || r.initiated_by_email || pid} (Initiator)`;
  }
  const ap = (r.approvers || []).find(a => a.user_id === pid || a.user_email === pid);
  if (ap) return ap.user_name || ap.user_email || pid;
  return formatUser(pid);
}

/**
 * Official Note Entry (ONE) — List View.
 *
 * Session-scoped filter memory: category / status / branch stick for
 * the current tab session via sessionStorage. Nothing persists across
 * a fresh login by design (per user checkpoint).
 *
 * Detail / create / edit / action UI opens as a separate top-level TAB
 * (via useFormTab) so the user can multi-task without losing their
 * list scroll/filter state.
 *
 * Pagination is server-side via the shared `usePaginatedList` hook
 * (reads `X-Total-Count` header) and `PaginationControls` component.
 */

const STATUS_COLORS = {
  Draft:            "bg-slate-100 text-slate-700 border-slate-300",
  Pending:          "bg-amber-50 text-amber-800 border-amber-300",
  Hold:             "bg-orange-50 text-orange-800 border-orange-300",
  "Query Pending":  "bg-violet-50 text-violet-800 border-violet-300",
  "Query Replied":  "bg-sky-50 text-sky-800 border-sky-300",
  Approved:         "bg-emerald-50 text-emerald-800 border-emerald-300",
  Rejected:         "bg-rose-50 text-rose-800 border-rose-300",
  Superseded:       "bg-slate-100 text-slate-500 border-slate-300 line-through",
};

const CATEGORY_META = {
  "Financial":     { color: "bg-indigo-50 text-indigo-800 border-indigo-300" },
  "Non-Financial": { color: "bg-teal-50 text-teal-800 border-teal-300" },
  "Policy Making": { color: "bg-purple-50 text-purple-800 border-purple-300" },
  "Other":         { color: "bg-slate-100 text-slate-700 border-slate-300" },
};

const SESSION_KEY = "one-notes.filters.v1";

function readSession() {
  try {
    return JSON.parse(sessionStorage.getItem(SESSION_KEY) || "{}");
  } catch { return {}; }
}

function writeSession(f) {
  try { sessionStorage.setItem(SESSION_KEY, JSON.stringify(f)); } catch { /* ignore */ }
}

function StatusBadge({ status }) {
  const cls = STATUS_COLORS[status] || STATUS_COLORS.Draft;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider border ${cls}`}
          data-testid={`one-status-${status.replace(/\s+/g, "-").toLowerCase()}`}>
      {status}
    </span>
  );
}

function CategoryBadge({ category }) {
  const meta = CATEGORY_META[category] || CATEGORY_META.Other;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider border ${meta.color}`}>
      {category}
    </span>
  );
}

function Th({ children, className = "", onClick, sortKey, currentSort, direction }) {
  const active = sortKey && currentSort === sortKey;
  const clickable = !!onClick;
  return (
    <th className={`py-2.5 px-3 text-left text-[10px] font-bold uppercase tracking-wider text-slate-600 ${clickable ? "cursor-pointer select-none" : ""} ${className}`}
        onClick={onClick}>
      <span className="inline-flex items-center gap-1">
        {children}
        {clickable && <ArrowUpDown size={10} className={active ? "text-[#F97316]" : "text-slate-400"} />}
        {active && <span className="text-[9px] text-[#F97316]">{direction === 1 ? "▲" : "▼"}</span>}
      </span>
    </th>
  );
}

/**
 * ONE Dashboard KPI tile row. Powered by /api/one/dashboard/summary.
 * Each tile is clickable and jumps to a preset filter combination so
 * "Pending With Me" → clicks straight into the corresponding tab, etc.
 */
const DASH_TONES = {
  amber:   { rail: "bg-amber-500",   bubble: "bg-amber-50 text-amber-700" },
  blue:    { rail: "bg-blue-500",    bubble: "bg-blue-50 text-blue-700" },
  slate:   { rail: "bg-slate-500",   bubble: "bg-slate-50 text-slate-700" },
  purple:  { rail: "bg-purple-500",  bubble: "bg-purple-50 text-purple-700" },
  emerald: { rail: "bg-emerald-500", bubble: "bg-emerald-50 text-emerald-700" },
  indigo:  { rail: "bg-indigo-500",  bubble: "bg-indigo-50 text-indigo-700" },
};

function DashTile({ title, value, hint, icon: Icon, tone = "blue", onClick, testid }) {
  const t = DASH_TONES[tone] || DASH_TONES.blue;
  const Tag = onClick ? "button" : "div";
  return (
    <Tag
      type={onClick ? "button" : undefined}
      onClick={onClick}
      data-testid={testid}
      className={`relative bg-white border border-slate-200 shadow-sm hover:shadow-md transition-shadow overflow-hidden ${onClick ? "cursor-pointer hover:-translate-y-0.5 transition-transform text-left w-full" : ""}`}
    >
      <div className={`absolute left-0 top-0 bottom-0 w-1 ${t.rail}`} />
      <div className="flex items-center gap-3 p-3 pl-4">
        <div className={`w-10 h-10 flex items-center justify-center shrink-0 ${t.bubble}`}>
          {Icon && <Icon size={18} />}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-[0.12em] text-slate-500 font-bold leading-tight">{title}</div>
          <div className="font-display text-[22px] font-black text-slate-900 mt-0.5 tabular-nums leading-tight">{value}</div>
          {hint && <div className="text-[10px] text-slate-500 mt-0.5 truncate">{hint}</div>}
        </div>
      </div>
    </Tag>
  );
}

function DashboardCards({ dash, onJump }) {
  if (!dash) return null;
  const t = dash.tiles || {};
  const bc = dash.by_category || {};
  return (
    <div className="mb-4" data-testid="one-dashboard-cards">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <DashTile title="Pending With Me" value={t.pending_with_me || 0} icon={Inbox} tone="amber"
          onClick={() => onJump("pending-me")} testid="one-kpi-pending-me" />
        <DashTile title="My Drafts" value={t.my_drafts || 0} icon={Files} tone="slate"
          onClick={() => onJump("my-drafts")} testid="one-kpi-my-drafts" />
        <DashTile title="Pending (All)" value={t.pending_all || 0} icon={Inbox} tone="blue"
          onClick={() => onJump("all", { status: "Pending" })} testid="one-kpi-pending-all" />
        <DashTile title="Active Policies" value={t.active_policies || 0} icon={ShieldCheck} tone="purple"
          onClick={() => onJump("policy-active")} testid="one-kpi-policies" />
        <DashTile title="Approved This Month" value={t.approved_this_month || 0} icon={CheckCircle2} tone="emerald"
          onClick={() => onJump("all", { status: "Approved" })} testid="one-kpi-approved-month" />
        <DashTile title="Total Notes" value={t.total_all || 0} icon={LayoutDashboard} tone="indigo"
          hint="All statuses in scope" testid="one-kpi-total" />
      </div>
      {/* By-category chips row */}
      <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px]">
        <span className="text-slate-500 uppercase tracking-wider font-bold">By Category:</span>
        {["Financial", "Non-Financial", "Policy Making", "Other"].map(c => (
          <button
            key={c}
            type="button"
            data-testid={`one-kpi-cat-${c.replace(/\s+/g, "-").toLowerCase()}`}
            onClick={() => onJump("all", { category: c })}
            className="inline-flex items-center gap-1 px-2 py-1 border border-slate-300 hover:border-[#F97316] hover:text-[#F97316] bg-white text-slate-700"
          >
            <span>{c}</span>
            <span className="font-mono text-slate-900">{bc[c] || 0}</span>
          </button>
        ))}
      </div>
    </div>
  );
}


export default function OneNotes() {
  const { user } = useAuth();
  useUserDirectory();
  const openFormTab = useFormTab();
  const uid = user?.id || user?.email || "";

  // Filters (session-scoped memory so navigating away & back preserves state).
  const [branches, setBranches] = useState([]);
  // Jul-2026 (user request) — ALWAYS land on "Pending With Me" so the
  // person can act immediately; "All" moved to the last tab.
  const [tab, setTab] = useState("pending-me");
  const [filters, setFilters] = useState(() => {
    const s = readSession();
    return {
      category: s.category || "",
      status:   s.status   || "",
      branch_id: s.branch_id || "",
      search:   s.search   || "",
    };
  });
  const [sort, setSort] = useState({ key: "submitted_at", dir: -1 });

  // Global search — debounced so we don't hammer the backend on every keystroke.
  const [searchDebounced, setSearchDebounced] = useState(filters.search || "");
  useEffect(() => {
    const t = setTimeout(() => setSearchDebounced((filters.search || "").trim()), 350);
    return () => clearTimeout(t);
  }, [filters.search]);

  // Persist filters (session only).
  useEffect(() => { writeSession({ ...filters, tab }); }, [filters, tab]);

  // Server-side pagination — the params object is memoised so the hook
  // only reloads when a filter value actually changes.
  const params = useMemo(() => {
    const p = {};
    if (filters.category)  p.category  = filters.category;
    if (filters.status)    p.status    = filters.status;
    if (filters.branch_id) p.branch_id = filters.branch_id;
    if (searchDebounced)   p.q = searchDebounced;
    if (tab === "pending-me")    p.pending_with = uid;
    if (tab === "my-drafts")     { p.initiator_id = uid; p.status = "Draft"; }
    if (tab === "policy-active") { p.category = "Policy Making"; p.active_only = true; }
    return p;
  }, [filters.category, filters.status, filters.branch_id, searchDebounced, tab, uid]);

  const {
    items: rows,
    total,
    page,
    pageSize,
    loading,
    setPage,
    setPageSize,
    reload,
  } = usePaginatedList("/one", params, 25);

  useEffect(() => {
    api.get("/branches").then(r => setBranches(r.data || [])).catch(() => setBranches([]));
  }, []);

  // Dashboard KPI tiles — cheap aggregated counts. Reloaded when the
  // branch filter changes OR when the list itself reloads (so an
  // action taken on the detail tab is reflected in the tile counts).
  const [dash, setDash] = useState(null);
  useEffect(() => {
    const params2 = filters.branch_id ? { branch_id: filters.branch_id } : {};
    api.get("/one/dashboard/summary", { params: params2 })
      .then(r => setDash(r.data))
      .catch(() => setDash(null));
  }, [filters.branch_id, total]);

  // Sort applied to the current page's rows. (Search + filter happen
  // server-side via `q` / params.)
  const sortedRows = useMemo(() => {
    const arr = [...rows];
    arr.sort((a, b) => {
      const av = (a[sort.key] || "");
      const bv = (b[sort.key] || "");
      if (av < bv) return -sort.dir;
      if (av > bv) return  sort.dir;
      return 0;
    });
    return arr;
  }, [rows, sort]);

  function toggleSort(k) {
    setSort(prev => prev.key === k ? { key: k, dir: -prev.dir } : { key: k, dir: -1 });
  }

  // Open create-note as a new top-level tab.
  function openNew() {
    openFormTab({
      path: "/one/new",
      label: "New ONE Note",
      icon: ClipboardEdit,
      parent: "/one",
      onSaved: reload,
      render: ({ close, saved, viewList }) => (
        <OneNotesDetail id={null} onClose={close} onSaved={saved} onViewList={viewList} />
      ),
    });
  }

  // Open a specific note as its own tab. `row` gives us enough info to
  // build a nice label like "NF-ONE/26-27/000016 · Subject…".
  function openRow(row) {
    const label = row.voucher_number
      ? `${row.voucher_number}${row.revision_no ? `·R${row.revision_no}` : ""}`
      : (row.subject ? `Draft · ${row.subject.slice(0, 20)}` : "Draft");
    openFormTab({
      path: `/one/${row.id}`,
      label: label,
      icon: Eye,
      parent: "/one",
      onSaved: reload,
      render: ({ close, saved, viewList }) => (
        <OneNotesDetail id={row.id} onClose={close} onSaved={saved} onViewList={viewList} />
      ),
    });
  }

  // Dashboard-tile click → jump to a specific tab (+ optional filter override).
  function jumpTo(nextTab, filterOverride = {}) {
    setTab(nextTab);
    setFilters(f => ({ ...f, ...filterOverride }));
  }

  return (
    <div>
      <Toaster richColors position="top-right" />
      <PageHeader
        eyebrow="Workflow"
        title="Official Note Entry (ONE)"
        subtitle="Digital replacement for the paper approval-note process. Supports L1–L5 sequential approval, queries, and Policy revisions."
        actions={
          <button
            data-testid="one-new-note-btn"
            onClick={openNew}
            className="inline-flex items-center gap-1.5 bg-[#F97316] hover:bg-[#EA580C] text-white px-4 py-2.5 text-xs uppercase tracking-wider font-bold"
          >
            <Plus size={13} /> New Note
          </button>
        }
      />

      {/* Dashboard KPI tiles (Phase 3) */}
      <DashboardCards dash={dash} onJump={jumpTo} />

      {/* Tabs */}
      <div className="border-b border-slate-200 mb-4 flex items-center gap-1 overflow-x-auto">
        {[
          { key: "pending-me",    label: "Pending With Me" },
          { key: "my-drafts",     label: "My Drafts" },
          { key: "policy-active", label: "Active Policies" },
          { key: "all",           label: "All" },
        ].map(t => (
          <button
            key={t.key}
            data-testid={`one-tab-${t.key}`}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2.5 text-xs font-bold uppercase tracking-wider border-b-2 transition-colors whitespace-nowrap ${
              tab === t.key ? "border-[#F97316] text-[#F97316]" : "border-transparent text-slate-500 hover:text-slate-900"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Filters */}
      <Card className="p-3 mb-4">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Field label="Search">
            <div className="relative">
              <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                data-testid="one-search"
                type="text"
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                placeholder="Voucher / Subject / Initiator / Project…"
                className={`${inputCls} pl-8`}
              />
            </div>
          </Field>
          <Field label="Category">
            <select
              data-testid="one-filter-category"
              value={filters.category}
              onChange={(e) => setFilters({ ...filters, category: e.target.value })}
              className={inputCls}
            >
              <option value="">All</option>
              <option value="Financial">Financial</option>
              <option value="Non-Financial">Non-Financial</option>
              <option value="Policy Making">Policy Making</option>
              <option value="Other">Other</option>
            </select>
          </Field>
          <Field label="Status">
            <select
              data-testid="one-filter-status"
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              className={inputCls}
            >
              <option value="">All</option>
              {Object.keys(STATUS_COLORS).map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="Branch">
            <select
              data-testid="one-filter-branch"
              value={filters.branch_id}
              onChange={(e) => setFilters({ ...filters, branch_id: e.target.value })}
              className={inputCls}
            >
              <option value="">All</option>
              {branches.map(b => <option key={b.id} value={b.id}>{b.branch_name || b.name}</option>)}
            </select>
          </Field>
          <Field label=" ">
            <button
              type="button"
              data-testid="one-refresh"
              onClick={reload}
              className="inline-flex items-center gap-1.5 border border-slate-300 hover:bg-slate-50 text-slate-700 px-3 py-2.5 text-xs uppercase tracking-wider font-bold w-full justify-center"
            >
              <RotateCw size={12} className={loading ? "animate-spin" : ""} /> Refresh
            </button>
          </Field>
        </div>
      </Card>

      {/* Table */}
      {sortedRows.length === 0 ? (
        loading ? (
          <EmptyState title="Loading notes…" />
        ) : (
          <EmptyState
            title="No notes found"
            hint="Nothing matches these filters. Try clearing the filters or start a new note."
            icon={ClipboardList}
          />
        )
      ) : (
        <Card className="overflow-hidden" data-testid="one-list-card">
          <div className="overflow-x-auto">
            <table className="min-w-full text-xs" data-testid="one-list-table">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <Th sortKey="voucher_number" onClick={() => toggleSort("voucher_number")}
                      currentSort={sort.key} direction={sort.dir}>Voucher #</Th>
                  <Th>Category</Th>
                  <Th sortKey="subject" onClick={() => toggleSort("subject")}
                      currentSort={sort.key} direction={sort.dir}>Subject</Th>
                  <Th>Branch</Th>
                  <Th>Project / Amount</Th>
                  <Th>Initiator</Th>
                  <Th sortKey="submitted_at" onClick={() => toggleSort("submitted_at")}
                      currentSort={sort.key} direction={sort.dir}>Submitted</Th>
                  <Th>Level</Th>
                  <Th>Status</Th>
                  <Th className="text-center">
                    <Paperclip size={11} className="inline text-slate-500" />
                  </Th>
                </tr>
              </thead>
              <tbody>
                {sortedRows.map(r => (
                  <tr key={r.id}
                      data-testid={`one-row-${r.id}`}
                      className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer"
                      onClick={() => openRow(r)}>
                    <td className="py-2.5 px-3 font-mono text-slate-900">
                      {r.voucher_number || <span className="italic text-slate-400">(draft)</span>}
                      {r.revision_no > 0 && (
                        <span className="ml-1 text-[10px] font-bold text-purple-700">R{r.revision_no}</span>
                      )}
                    </td>
                    <td className="py-2.5 px-3"><CategoryBadge category={r.category} /></td>
                    <td className="py-2.5 px-3 max-w-[320px] truncate" title={r.subject}>{r.subject}</td>
                    <td className="py-2.5 px-3 text-slate-700">{r.branch_name || "—"}</td>
                    <td className="py-2.5 px-3 text-slate-700">
                      {r.category === "Financial" ? (
                        <div>
                          <div className="text-slate-900 truncate max-w-[160px]" title={r.project_name}>{r.project_name || "—"}</div>
                          <div className="font-mono text-[11px] text-[#F97316]">{formatINR(r.amount || 0)}</div>
                        </div>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>
                    <td className="py-2.5 px-3 text-slate-700 truncate max-w-[180px]" title={r.initiated_by_email}>
                      <div>{r.initiated_by_name || "—"}</div>
                      <div className="text-[10px] text-slate-500">{r.initiated_by_email}</div>
                    </td>
                    <td className="py-2.5 px-3 text-slate-600 text-[11px]">
                      {r.submitted_at ? new Date(r.submitted_at).toLocaleString() : "—"}
                    </td>
                    <td className="py-2.5 px-3 text-slate-700 font-mono text-center">
                      {r.status === "Pending" || r.status === "Query Pending" || r.status === "Query Replied" || r.status === "Hold"
                        ? `L${r.current_level || 1}/${(r.approvers || []).length}`
                        : "—"}
                    </td>
                    <td className="py-2.5 px-3">
                      <StatusBadge status={r.status} />
                      {["Pending", "Hold", "Query Pending", "Query Replied"].includes(r.status) && pendingWithName(r) && (
                        <div className="text-[10px] text-slate-500 mt-1 whitespace-nowrap" data-testid={`one-pending-with-${r.id}`}>
                          with <span className={`font-bold ${pendingWithName(r).includes("(Initiator)") ? "text-violet-700" : "text-slate-700"}`}>{pendingWithName(r)}</span>
                        </div>
                      )}
                    </td>
                    <td className="py-2.5 px-3 text-center">
                      {(r.attachments || []).length > 0 ? (
                        <span
                          className="inline-flex items-center gap-0.5 text-slate-600 text-[11px]"
                          data-testid={`one-att-${r.id}`}
                        >
                          <Paperclip size={11} /> {(r.attachments || []).length}
                        </span>
                      ) : ""}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <PaginationControls
            page={page}
            pageSize={pageSize}
            total={total}
            loading={loading}
            onPage={setPage}
            onPageSize={setPageSize}
            pageSizeOptions={[25, 50, 100, 200]}
          />
        </Card>
      )}
    </div>
  );
}
